import React, { useEffect } from 'react';
import LegalSidebar from '../components/LegalSidebar';
import { setPageSEO } from '../utils/seo.js';
import './Terms.css';

function PrivacyPolicy() {
  useEffect(() => setPageSEO({
    title: 'Privacy Policy - Clientalio',
    description: 'Learn how Clientalio collects, stores, processes, and protects your personal and testimonial-related information.',
    canonical: 'https://clientalio.com/privacy-policy',
    ogTitle: 'Privacy Policy - Clientalio',
    ogDescription: 'Learn how Clientalio collects, stores, processes, and protects your personal and testimonial-related information.',
    twitterTitle: 'Privacy Policy - Clientalio',
    twitterDescription: 'Learn how Clientalio collects, stores, processes, and protects your personal and testimonial-related information.'
  }), []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="terms-page">
      <div className="container">
        <div className="terms-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: 06-Oct-2025</p>
        </div>
        <div className="terms-layout">
          <LegalSidebar activePage="privacy-policy" />

          {/* Main Content */}
          <div className="terms-content">
            <div className="content-section">

              <section className="terms-section">
                <h2>1. Introduction</h2>
                <p>
                  Welcome to Clientalio. We respect your privacy and are committed to protecting
                  your personal data. This Privacy Policy explains how we collect, use, store, and
                  protect information when you visit our website or use our services.
                </p>
              </section>

              <section className="terms-section">
                <h2>2. Data We Collect</h2>
                <p>
                  We may collect the following types of information:
                </p>
                <ul>
                  <li><strong>Personal Information:</strong> Name, email address, company name, and testimonial content that you provide directly</li>
                  <li><strong>Automatically Collected Data:</strong> IP address, browser type, device information, and usage data</li>
                  <li><strong>Cookies and Tracking:</strong> See our Cookie Policy for detailed information about cookies and tracking technologies</li>
                </ul>
              </section>

              <section className="terms-section">
                <h2>3. How We Use Your Data</h2>
                <p>
                  We use personal data to:
                </p>
                <ul>
                  <li>Deliver and improve our services</li>
                  <li>Manage your account and subscription</li>
                  <li>Respond to support requests and customer inquiries</li>
                  <li>Send important service updates and notifications (not marketing)</li>
                  <li>Analyze service performance and user behavior</li>
                  <li>Ensure platform security and prevent fraud</li>
                </ul>
                <p>
                  We never use your data for unauthorized marketing purposes or sell it to third parties.
                </p>
              </section>

              <section className="terms-section">
                <h2>4. How We Store and Protect Your Data</h2>
                <p>
                  All data is securely stored on cloud servers with industry-standard encryption
                  and security measures. We implement:
                </p>
                <ul>
                  <li>End-to-end encryption for sensitive data</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Access controls and authentication protocols</li>
                  <li>Secure data backup and recovery procedures</li>
                </ul>
                <p>
                  We engage third parties only under applicable data protection contracts and ensure
                  they comply with GDPR and other data protection standards.
                </p>
              </section>

              <section className="terms-section">
                <h2>5. Data Sharing and Disclosure</h2>
                <p>
                  We do not sell, trade, or rent your personal information to third parties. We may
                  share data only in the following circumstances:
                </p>
                <ul>
                  <li>With trusted service providers who assist in operating our platform</li>
                  <li>When required by law or to protect our legal rights</li>
                  <li>In connection with a business transfer or acquisition</li>
                  <li>With your explicit consent</li>
                </ul>
              </section>

              <section className="terms-section">
                <h2>6. Your Data Rights</h2>
                <p>
                  Under GDPR and other privacy laws, you have the right to:
                </p>
                <ul>
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data</li>
                  <li><strong>Restrict Processing:</strong> Limit how we use your data</li>
                  <li><strong>Data Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Object:</strong> Object to certain types of data processing</li>
                </ul>
                <p>
                  To exercise any of these rights, contact us at <a href="mailto:support@clientalio.com">support@clientalio.com</a>
                </p>
              </section>

              <section className="terms-section">
                <h2>7. Data Retention</h2>
                <p>
                  We retain your personal data only for as long as necessary to provide our services
                  and comply with legal obligations. When data is no longer needed, it is securely
                  deleted or anonymized.
                </p>
              </section>

              <section className="terms-section">
                <h2>8. Children's Privacy</h2>
                <p>
                  Our services are not intended for children under 16 years of age. We do not
                  knowingly collect personal information from children. If you believe we have
                  collected data from a child, please contact us immediately.
                </p>
              </section>

              <section className="terms-section">
                <h2>9. Changes to Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. Changes will be posted on
                  this page with an updated "Last Updated" date. We encourage you to review this
                  policy periodically.
                </p>
              </section>

              <section className="terms-section">
                <h2>10. Contact Us</h2>
                <p>
                  For any questions or concerns about this Privacy Policy or our data practices:
                </p>
                <ul>
                  <li>Email: <a href="mailto:support@clientalio.com">support@clientalio.com</a></li>
                  <li>Legal: <a href="mailto:legal@clientalio.com">legal@clientalio.com</a></li>
                </ul>
                <p>
                  Registered Office: Rohini, New Delhi, India
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
